import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PageHeader, DataTable, EmptyState } from "@/components/ui-system";
import { toast } from "sonner";
import {
  Scale, RefreshCw, Loader2, AlertTriangle, ExternalLink, ArrowRight, Play, CheckCircle2, MinusCircle, Wallet, Download, Eye,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

// Rate-error lifecycle colours (local map — this status vocabulary is domain-specific).
const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-700",
  under_review: "bg-blue-100 text-blue-700",
  claimed: "bg-indigo-100 text-indigo-700",
  waived: "bg-slate-100 text-slate-600",
  resolved: "bg-green-100 text-green-700",
};
// What an admin can do next, given the current status.
const NEXT_ACTIONS = {
  open: [["mark_under_review", "Mark under review"], ["mark_claimed", "Raised with Delhivery"], ["waive", "Waive (accept charge)"], ["resolve", "Mark resolved"]],
  under_review: [["mark_claimed", "Raised with Delhivery"], ["waive", "Waive (accept charge)"], ["resolve", "Mark resolved"]],
  claimed: [["waive", "Waive (accept charge)"], ["resolve", "Mark resolved"], ["reopen", "Re-open"]],
  waived: [["reopen", "Re-open"]],
  resolved: [["reopen", "Re-open"]],
};
// Orders that carry a freight bill worth validating.
const SCAN_STATUS_OPTIONS = [
  "DELIVERED", "RETURN_DELIVERED", "PART_DEL", "RETURNED_INTRANSIT",
  "RECEIVED_AT_RETURN_CENTER", "RETURN_OFD", "LOST",
].map((s) => ({ value: s, label: s }));

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Human labels for the scan's skip reason_codes (why an order couldn't be rate-checked).
const REASON_LABELS = {
  rate_unresolved: "Unmapped zone / lane",
  at_minimum_charge: "At minimum charge",
  fuel_hike_applied: "Fuel hike present",
  ambiguous_recovery: "Ambiguous recovery",
  no_rate_card_for_date: "No rate card for date",
  no_freight_charges: "No freight charges",
};
const reasonLabel = (code) => REASON_LABELS[code] || (code || "unknown").replace(/_/g, " ");

const RateErrors = ({ user, onLogout }) => {
  // ── list state ──
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState({ open_count: 0, open_net_overcharge: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [sellers, setSellers] = useState([]);

  // ── top-level view: "scan" (scan + error lists) | "rectify" (seller freight rectification) ──
  const [mainView, setMainView] = useState("scan");

  // ── matched / verified LRNs (second tab) ──
  const [activeTab, setActiveTab] = useState("errors");   // "errors" | "matched" | "skipped"
  const [matches, setMatches] = useState([]);
  const [matchCount, setMatchCount] = useState(0);
  const [matchesLoading, setMatchesLoading] = useState(false);

  // ── skipped orders (third tab) ──
  const [skipped, setSkipped] = useState([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [reasonBreakdown, setReasonBreakdown] = useState([]);
  const [reasonFilter, setReasonFilter] = useState("");   // reason_code chip filter
  const [skippedLoading, setSkippedLoading] = useState(false);

  // ── seller freight rectification (agreement-based recompute + wallet adjustment) ──
  const [rectifyMode, setRectifyMode] = useState("single");   // "single" | "bulk"
  const [rectifyRef, setRectifyRef] = useState("");
  const [rectifyPreview, setRectifyPreview] = useState(null);
  const [rectifyLoading, setRectifyLoading] = useState(false);
  const [rectifyApplying, setRectifyApplying] = useState(false);
  // bulk (all a seller's orders)
  const [bulkSeller, setBulkSeller] = useState("");
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());   // order_ids ticked for selective apply
  const [detailModalRow, setDetailModalRow] = useState(null);   // row whose full bifurcation modal is open
  const [rowDetail, setRowDetail] = useState({});         // order_id -> single-order preview (full breakdown)
  const [exporting, setExporting] = useState(false);     // CSV export in progress (fetching bifurcations)

  // ── scan state ──
  const [scanStatuses, setScanStatuses] = useState(["DELIVERED", "RETURN_DELIVERED"]);
  const [scanSeller, setScanSeller] = useState("");
  const [scanStart, setScanStart] = useState("");
  const [scanEnd, setScanEnd] = useState("");
  const [scanCount, setScanCount] = useState(null);
  const [scanCap, setScanCap] = useState({ cap: 2000, over_cap: false });
  const [executing, setExecuting] = useState(false);
  const [activeScanId, setActiveScanId] = useState(null);
  const [scanProgress, setScanProgress] = useState(null);

  // ── detail dialog ──
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);   // full freight bifurcation (live-fetched)
  const [bdLoading, setBdLoading] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [applying, setApplying] = useState(false);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sellerFilter) params.seller_id = sellerFilter;
      const res = await axios.get(`${API}/rate-validation/errors`, { params });
      setErrors(res.data.errors || []);
      setSummary({
        open_count: res.data.open_count || 0,
        open_net_overcharge: res.data.open_net_overcharge || 0,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load rate errors");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sellerFilter]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  // Verified/matched LRNs — same seller filter as the errors tab; loaded on mount (for the
  // tab count) and whenever the seller filter changes.
  const fetchMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const params = {};
      if (sellerFilter) params.seller_id = sellerFilter;
      const res = await axios.get(`${API}/rate-validation/matches`, { params });
      setMatches(res.data.matches || []);
      setMatchCount(res.data.match_count || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load matched LRNs");
    } finally {
      setMatchesLoading(false);
    }
  }, [sellerFilter]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  // Skipped orders — seller filter + an optional reason_code chip filter; the reason breakdown
  // is returned alongside so the chips reflect the full (seller-scoped) set.
  const fetchSkipped = useCallback(async () => {
    setSkippedLoading(true);
    try {
      const params = {};
      if (sellerFilter) params.seller_id = sellerFilter;
      if (reasonFilter) params.reason = reasonFilter;
      const res = await axios.get(`${API}/rate-validation/skipped`, { params });
      setSkipped(res.data.skipped || []);
      setSkippedCount(res.data.skipped_count || 0);
      setReasonBreakdown(res.data.reason_breakdown || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load skipped orders");
    } finally {
      setSkippedLoading(false);
    }
  }, [sellerFilter, reasonFilter]);

  useEffect(() => { fetchSkipped(); }, [fetchSkipped]);

  // Seller freight rectification — preview recomputes the seller charge from the agreement
  // SELL card; apply posts the atomic +/- wallet adjustment after admin confirmation.
  const previewRectify = async () => {
    if (!rectifyRef.trim()) return;
    setRectifyLoading(true);
    setRectifyPreview(null);
    try {
      const r = await axios.post(`${API}/rate-validation/rectify/preview`, { order_ref: rectifyRef.trim() });
      setRectifyPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not price this order from the agreement");
    } finally {
      setRectifyLoading(false);
    }
  };

  const applyRectify = async () => {
    if (!rectifyPreview) return;
    const diff = rectifyPreview.difference;
    const verb = diff > 0 ? `debit ₹${Math.abs(diff).toFixed(2)} from` : `credit ₹${Math.abs(diff).toFixed(2)} to`;
    if (!window.confirm(`Confirm agreement rectification for ${rectifyPreview.order_number}?\n\nThis will ${verb} ${rectifyPreview.seller_name}'s wallet (₹${rectifyPreview.old.seller_final_amount.toFixed(2)} → ₹${rectifyPreview.new.seller_final_amount.toFixed(2)}).`)) return;
    setRectifyApplying(true);
    try {
      const r = await axios.post(`${API}/rate-validation/rectify/apply`, {
        order_id: rectifyPreview.order_id,
        expected_new_final_amount: rectifyPreview.expected_new_final_amount,
      });
      toast.success(r.data.message || "Rectification applied");
      // refresh the preview to reflect the now-rectified state
      previewRectify();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rectification failed");
    } finally {
      setRectifyApplying(false);
    }
  };

  // Bulk rectification (all a seller's eligible forward orders).
  const previewBulk = async () => {
    if (!bulkSeller) { toast.error("Select a seller"); return; }
    setBulkLoading(true);
    setBulkPreview(null);
    try {
      const r = await axios.post(`${API}/rate-validation/rectify/bulk/preview`, {
        seller_id: bulkSeller, start_date: bulkStart || null, end_date: bulkEnd || null,
      });
      setBulkPreview(r.data);
      setSelectedIds(new Set());
      setDetailModalRow(null);
      setRowDetail({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not preview the bulk rectification");
    } finally {
      setBulkLoading(false);
    }
  };

  const executeBulk = async () => {
    if (!bulkPreview) return;
    const net = bulkPreview.net_difference;
    const verb = net > 0 ? `debit a net ₹${Math.abs(net).toFixed(2)} from` : `credit a net ₹${Math.abs(net).toFixed(2)} to`;
    if (!window.confirm(`Apply the agreement to ${bulkPreview.pricable_count} order(s) for ${bulkPreview.seller_name}?\n\nThis will ${verb} their wallet (${bulkPreview.to_debit} debit, ${bulkPreview.to_credit} credit, ${bulkPreview.no_change} no-change).`)) return;
    try {
      const r = await axios.post(`${API}/rate-validation/rectify/bulk/execute`, {
        seller_id: bulkSeller, start_date: bulkStart || null, end_date: bulkEnd || null,
      });
      setBulkProgress({ status: "pending", total_orders: r.data.total_orders, processed_orders: 0 });
      setBulkJobId(r.data.id);
      toast.success(`Rectifying ${r.data.total_orders} order(s)…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the bulk rectification");
    }
  };

  // --- per-order selection (selective apply) ---
  const selectableIds = () =>
    (bulkPreview?.rows || []).filter((r) => r.pricable && Math.abs(r.difference) > 0.01).map((r) => r.order_id);

  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => setSelectedIds((prev) => {
    const ids = selectableIds();
    const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
    return allOn ? new Set() : new Set(ids);
  });

  const executeBulkSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !bulkPreview) return;
    if (!window.confirm(`Apply the agreement to ${ids.length} selected order(s) for ${bulkPreview.seller_name}?\n\nThis moves wallet money for only the ticked orders.`)) return;
    try {
      const r = await axios.post(`${API}/rate-validation/rectify/bulk/execute`, {
        seller_id: bulkSeller, order_ids: ids,
      });
      setBulkProgress({ status: "pending", total_orders: r.data.total_orders, processed_orders: 0 });
      setBulkJobId(r.data.id);
      toast.success(`Rectifying ${r.data.total_orders} selected order(s)…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the rectification");
    }
  };

  // Export the per-order detail as CSV, including the complete NEW-agreement bifurcation
  // (one row per component + the pre-tax/GST/total/current/difference summary lines per order).
  const exportBulkCsv = async () => {
    const rows = bulkPreview?.rows || [];
    if (!rows.length) return;
    setExporting(true);
    try {
      const detailMap = await ensureBreakdowns(rows);   // fetch any missing bifurcations (bounded concurrency)
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const header = ["Order", "LRN", "Status", "Lane", "Billed Wt kg", "Line", "Agreement term", "Amount"];
      const out = [];
      rows.forEach((r) => {
        const lane = r.pricable ? `${r.lane?.origin_zone} → ${r.lane?.dest_zone} · ₹${r.lane?.base_rate}/kg` : "";
        const wt = r.lane?.billed_weight ?? "";
        const base = [r.order_number, r.lrn, r.status, lane, wt];
        if (!r.pricable) {
          out.push([...base, "Skipped", r.message || r.reason || "", ""]);
          return;
        }
        const d = detailMap[r.order_id];
        const fc = d?.new?.seller_charges?.fwd_charges || {};
        const bd = fc.breakdown || {};
        const term = (v) => [v?.basis, v?.remark].filter(Boolean).join(" — ");
        Object.entries(bd).forEach(([k, v]) => {
          out.push([...base, k.replace(/_/g, " "), term(v), v?.seller_value ?? ""]);
        });
        out.push([...base, "Pre-tax", "", fc.pre_tax_freight ?? ""]);
        out.push([...base, `GST (${fc.gst_percent ?? ""}%)`, "", fc.gst ?? ""]);
        out.push([...base, "Total (agreement)", "", d?.new?.seller_final_amount ?? r.new]);
        out.push([...base, "Current (billed)", "", r.old]);
        out.push([...base, "Difference", r.difference > 0 ? "debit from seller" : r.difference < 0 ? "credit to seller" : "no change", r.difference]);
      });
      const csv = "﻿" + [header, ...out].map((line) => line.map(esc).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rectification_${(bulkPreview.seller_name || bulkSeller || "seller").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not build the export");
    } finally {
      setExporting(false);
    }
  };

  // open a row's full bifurcation in a modal — lazily fetch the single-order preview (full breakdown)
  const openRowDetail = async (r) => {
    if (!r.pricable) return;
    const id = r.order_id;
    setDetailModalRow(r);
    if (!rowDetail[id]) {
      try {
        const res = await axios.post(`${API}/rate-validation/rectify/preview`, { order_ref: id });
        setRowDetail((prev) => ({ ...prev, [id]: res.data }));
      } catch {
        toast.error("Couldn't load the breakdown");
      }
    }
  };

  // Ensure the full bifurcation is loaded for a set of rows (bounded concurrency), returning a
  // merged order_id -> preview map (used by the CSV export so every order carries its bifurcation).
  const ensureBreakdowns = async (rows) => {
    const need = rows.filter((r) => r.pricable && !rowDetail[r.order_id]);
    if (!need.length) return rowDetail;
    const fetched = {};
    const CONC = 6;
    for (let i = 0; i < need.length; i += CONC) {
      const batch = need.slice(i, i + CONC);
      const got = await Promise.all(batch.map((r) =>
        axios.post(`${API}/rate-validation/rectify/preview`, { order_ref: r.order_id })
          .then((res) => ({ id: r.order_id, data: res.data }))
          .catch(() => ({ id: r.order_id, data: null }))));
      got.forEach(({ id, data }) => { if (data) fetched[id] = data; });
    }
    setRowDetail((prev) => ({ ...prev, ...fetched }));
    return { ...rowDetail, ...fetched };
  };

  // poll the bulk rectification job
  useEffect(() => {
    if (!bulkJobId) return;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/bulk-operations/history/${bulkJobId}`);
        setBulkProgress(r.data);
        if (r.data.status === "completed" || r.data.status === "failed") {
          setBulkJobId(null);
          if (r.data.status === "completed") {
            toast.success(`Done — ${r.data.successful_orders || 0} adjusted, ${r.data.skipped_orders || 0} skipped`);
            previewBulk();   // refresh the summary (now mostly no-change)
          } else {
            toast.error("Bulk rectification failed — see Bulk Operations");
          }
        }
      } catch { /* transient */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
    // NOTE: intentionally depends only on bulkJobId. No react-hooks/exhaustive-deps
    // disable directive — this project's ESLint config doesn't load that plugin, so
    // CRA fails the CI build with "rule ... was not found" (see GlideSelect.jsx).
  }, [bulkJobId]);

  useEffect(() => {
    axios.get(`${API}/bulk-operations/sellers`)
      .then((r) => setSellers(r.data || []))
      .catch(() => setSellers([]));
  }, []);

  // Count preview — re-run whenever the scan filter changes.
  useEffect(() => {
    let cancelled = false;
    const payload = {
      statuses: scanStatuses,
      seller_id: scanSeller || null,
      start_date: scanStart || null,
      end_date: scanEnd || null,
    };
    if (!scanStatuses.length) { setScanCount(0); return; }
    axios.post(`${API}/rate-validation/scan/count`, payload)
      .then((r) => { if (!cancelled) { setScanCount(r.data.count); setScanCap({ cap: r.data.cap, over_cap: r.data.over_cap }); } })
      .catch(() => { if (!cancelled) setScanCount(null); });
    return () => { cancelled = true; };
  }, [scanStatuses, scanSeller, scanStart, scanEnd]);

  // Poll the running scan (reuses the bulk-operation history doc).
  useEffect(() => {
    if (!activeScanId) return;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/bulk-operations/history/${activeScanId}`);
        setScanProgress(r.data);
        if (r.data.status === "completed" || r.data.status === "failed") {
          setActiveScanId(null);
          fetchErrors();
          fetchMatches();
          fetchSkipped();
          if (r.data.status === "completed") {
            toast.success(`Scan done — ${r.data.mismatch_count || 0} rate error(s), ${rupee(r.data.net_overcharge)} net`);
          } else {
            toast.error("Scan failed — see bulk operations");
          }
        }
      } catch {
        /* transient — keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [activeScanId, fetchErrors, fetchMatches, fetchSkipped]);

  const runScan = async () => {
    setExecuting(true);
    try {
      const res = await axios.post(`${API}/rate-validation/scan/execute`, {
        statuses: scanStatuses,
        seller_id: scanSeller || null,
        start_date: scanStart || null,
        end_date: scanEnd || null,
      });
      setScanProgress({ status: "pending", total_orders: res.data.total_orders, processed_orders: 0 });
      setActiveScanId(res.data.id);
      toast.success(`Scanning ${res.data.total_orders} order(s)…`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start scan");
    } finally {
      setExecuting(false);
    }
  };

  const openDetail = (row, opts = {}) => {
    setDetail({ ...row, __matched: !!opts.matched });
    setActionType("");
    setActionNotes("");
    setDetailOpen(true);
    // Live-fetch the full freight bifurcation for this order (display-only; same card/engine).
    setBreakdown(null);
    if (row.order_id) {
      setBdLoading(true);
      axios.get(`${API}/rate-validation/order/${row.order_id}`)
        .then((r) => setBreakdown(r.data?.breakdown || null))
        .catch(() => setBreakdown(null))
        .finally(() => setBdLoading(false));
    }
  };

  const applyAction = async () => {
    if (!detail || !actionType) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/rate-validation/errors/${detail.id}/action`, {
        action_type: actionType, notes: actionNotes || null,
      });
      toast.success("Updated");
      setDetail(r.data);
      setActionType("");
      setActionNotes("");
      fetchErrors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setApplying(false);
    }
  };

  const progressPct = scanProgress && scanProgress.total_orders
    ? Math.min(100, Math.round((scanProgress.processed_orders / scanProgress.total_orders) * 100))
    : 0;

  const columns = [
    {
      key: "order", header: "Order", primary: true,
      cell: (r) => (
        <div>
          <a href={`/admin/orders/${r.order_id}`} target="_blank" rel="noopener noreferrer"
             className="font-medium text-[#00A699] hover:underline inline-flex items-center gap-1">
            {r.order_number || r.order_id}<ExternalLink className="w-3 h-3" />
          </a>
          {r.lrn && <div className="text-xs text-slate-500">LRN {r.lrn}</div>}
        </div>
      ),
    },
    { key: "seller", header: "Seller", hideOnMobile: true, cell: (r) => <span className="text-sm">{r.seller_name || r.seller_id || "—"}</span> },
    {
      key: "lane", header: "Lane", hideOnMobile: true,
      cell: (r) => (
        <div className="text-sm">
          <span className="inline-flex items-center gap-1 font-medium">
            {r.origin_zone || "?"}<ArrowRight className="w-3 h-3 text-slate-400" />{r.dest_zone || "?"}
          </span>
          <div className="text-xs text-slate-400">{r.rate_source} · ₹{r.base_rate}/kg · {r.billed_weight}kg</div>
        </div>
      ),
    },
    {
      key: "net_overcharge", header: "Net overcharge", align: "right",
      cell: (r) => (
        <span className={`font-semibold ${r.net_overcharge > 0 ? "text-red-600" : "text-green-600"}`}>
          {r.net_overcharge > 0 ? "+" : ""}{rupee(r.net_overcharge)}
        </span>
      ),
    },
    {
      key: "components", header: "Mismatch", hideOnMobile: true,
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.mismatched_components || []).map((c) => (
            <Badge key={c.component} variant="outline" className="text-xs">{c.component}</Badge>
          ))}
        </div>
      ),
    },
    { key: "status", header: "Status", cell: (r) => <Badge className={STATUS_COLORS[r.status] || "bg-slate-100"}>{(r.status || "").replace("_", " ").toUpperCase()}</Badge> },
    {
      key: "actions", header: "", isActions: true,
      cell: (r) => <Button size="sm" variant="outline" onClick={() => openDetail(r)}>Review</Button>,
    },
  ];

  // Matched / verified LRNs — the agreement calculation vs what Delhivery charged, side by side.
  const matchColumns = [
    columns[0],                 // Order (+ LRN)
    columns[1],                 // Seller
    columns[2],                 // Lane
    {
      key: "agreement_total", header: "Agreement", align: "right",
      cell: (r) => <span className="text-sm">{r.agreement_total == null ? "—" : rupee(r.agreement_total)}</span>,
    },
    {
      key: "delhivery_total", header: "Delhivery", align: "right",
      cell: (r) => <span className="text-sm">{r.delhivery_total == null ? "—" : rupee(r.delhivery_total)}</span>,
    },
    {
      key: "net_delta", header: "Δ total", align: "right", hideOnMobile: true,
      cell: (r) => (
        r.net_delta == null ? <span className="text-slate-300">—</span> : (
          <span className={`text-sm font-medium ${r.net_delta > 0.01 ? "text-red-600" : r.net_delta < -0.01 ? "text-green-600" : "text-slate-400"}`}>
            {r.net_delta > 0 ? "+" : ""}{rupee(r.net_delta)}
          </span>
        )
      ),
    },
    {
      key: "status", header: "Status",
      cell: () => <Badge className="bg-green-100 text-green-700">MATCHED</Badge>,
    },
    {
      key: "actions", header: "", isActions: true,
      cell: (r) => <Button size="sm" variant="outline" onClick={() => openDetail(r, { matched: true })}>Review</Button>,
    },
  ];

  // Skipped orders — the order, the lane/weight we could resolve (if any), and WHY it was skipped.
  const skippedColumns = [
    columns[0],                 // Order (+ LRN)
    columns[1],                 // Seller
    {
      key: "lane", header: "Lane", hideOnMobile: true,
      cell: (r) => (
        <div className="text-sm">
          <span className="inline-flex items-center gap-1 font-medium">
            {r.origin_zone || "?"}<ArrowRight className="w-3 h-3 text-slate-400" />{r.dest_zone || "?"}
          </span>
          {r.billed_weight != null && <div className="text-xs text-slate-400">{r.billed_weight}kg</div>}
        </div>
      ),
    },
    {
      key: "reason", header: "Reason",
      cell: (r) => (
        <div>
          <Badge className="bg-amber-100 text-amber-700">{reasonLabel(r.reason_code)}</Badge>
          {r.reason && r.reason.includes(":") && (
            <div className="text-xs text-slate-400 mt-0.5">{r.reason.split(":").slice(1).join(":")}</div>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Rate Errors"
          subtitle="Delhivery charges that diverge from the signed agreement rate card — component by component"
          actions={
            <Button variant="outline" size="sm" onClick={fetchErrors}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          }
        />

        {/* View switcher — scan/error tools vs the seller freight rectification tool */}
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-fit">
          <button type="button" onClick={() => setMainView("scan")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mainView === "scan" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
            <Scale className="w-4 h-4" /> Scan &amp; errors
          </button>
          <button type="button" onClick={() => setMainView("rectify")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mainView === "rectify" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
            <Wallet className="w-4 h-4" /> Seller freight rectification
          </button>
        </div>

        {mainView === "scan" && (
        <>
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-sm text-slate-600">Open rate errors</span>
              <span className="text-2xl font-bold text-amber-600">{summary.open_count}</span>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-sm text-slate-600">Net overcharge (open)</span>
              <span className="text-2xl font-bold text-red-600">{rupee(summary.open_net_overcharge)}</span>
            </CardContent>
          </Card>
        </div>

        {/* Scan */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="w-5 h-5" style={{ color: "#00A699" }} /> Scan orders for rate errors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs">Order statuses</Label>
                <SearchableMultiSelect
                  options={SCAN_STATUS_OPTIONS}
                  values={scanStatuses}
                  onChange={setScanStatuses}
                  placeholder="Select statuses…"
                />
              </div>
              <div>
                <Label className="text-xs">Seller (optional)</Label>
                <Select value={scanSeller || "all"} onValueChange={(v) => setScanSeller(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sellers</SelectItem>
                    {sellers.map((s) => (
                      <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.company_registered_name || s.seller_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={scanStart} onChange={(e) => setScanStart(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={scanEnd} onChange={(e) => setScanEnd(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                {scanCount === null ? "—" : (
                  <>
                    <span className="font-semibold text-slate-900">{scanCount.toLocaleString("en-IN")}</span>{" "}
                    order(s) with freight match
                    {scanCap.over_cap && <span className="text-amber-600"> — capped at {scanCap.cap.toLocaleString("en-IN")}, narrow the window</span>}
                  </>
                )}
              </div>
              <Button
                onClick={runScan}
                disabled={executing || !!activeScanId || !scanStatuses.length || !scanCount || scanCap.over_cap}
                style={{ backgroundColor: "#00A699" }} className="hover:opacity-90"
              >
                {executing || activeScanId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                {activeScanId ? "Scanning…" : "Run scan"}
              </Button>
            </div>

            {/* Live progress */}
            {scanProgress && (
              <div className="rounded-lg bg-slate-50 p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize">{scanProgress.status}</span>
                  <span className="text-slate-600">{scanProgress.processed_orders || 0} / {scanProgress.total_orders || 0}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-[#00A699] transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="flex flex-wrap gap-4 text-xs pt-1">
                  <span className="text-red-600">{scanProgress.mismatch_count || 0} rate error(s)</span>
                  <span className="text-green-600">{scanProgress.successful_orders || 0} matched</span>
                  <span className="text-yellow-600">{scanProgress.skipped_orders || 0} skipped</span>
                  <span className="text-slate-600">{rupee(scanProgress.net_overcharge)} net overcharge</span>
                </div>
                {scanProgress.id && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 pt-1 border-t border-slate-200/70">
                    <span>Bulk job ID</span>
                    <code className="font-mono text-slate-700 bg-slate-100 rounded px-1.5 py-0.5">{scanProgress.id}</code>
                    <a href="/admin/bulk-operations" target="_blank" rel="noopener noreferrer"
                       className="text-[#00A699] hover:underline inline-flex items-center gap-0.5">
                      Bulk Operations <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}

        {/* Seller freight rectification — recompute the seller charge from the agreement card + adjust wallet */}
        {mainView === "rectify" && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="w-5 h-5" style={{ color: "#00A699" }} /> Seller freight rectification
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Recompute what a seller should be charged from their signed SELL rate card (the agreement), then confirm a wallet adjustment. Look up by LRN, order number, or order id.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* mode toggle */}
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-fit">
              <button type="button" onClick={() => setRectifyMode("single")}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${rectifyMode === "single" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                Single order
              </button>
              <button type="button" onClick={() => setRectifyMode("bulk")}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${rectifyMode === "bulk" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                All orders for a seller
              </button>
            </div>

            {rectifyMode === "single" && (
            <>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="LRN / order number / order id"
                value={rectifyRef}
                onChange={(e) => setRectifyRef(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") previewRectify(); }}
                className="sm:max-w-sm"
              />
              <Button onClick={previewRectify} disabled={rectifyLoading || !rectifyRef.trim()} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                {rectifyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Preview
              </Button>
            </div>

            {rectifyPreview && !rectifyPreview.pricable && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
                <div className="font-medium text-slate-900">
                  {rectifyPreview.order_number}{rectifyPreview.lrn ? ` · LRN ${rectifyPreview.lrn}` : ""} · {rectifyPreview.seller_name || rectifyPreview.seller_id}
                </div>
                <div className="text-amber-700 mt-1 inline-flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {rectifyPreview.message}
                </div>
                {rectifyPreview.reason === "no_card" && (
                  <div className="text-xs text-slate-500 mt-1">Upload the seller's agreement in <span className="font-medium">Seller Accounts → Rate card</span> to price this order.</div>
                )}
              </div>
            )}

            {rectifyPreview && rectifyPreview.pricable && (
              <div className="rounded-lg border p-4 space-y-4">
                {/* header */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-medium text-slate-900">{rectifyPreview.order_number}</span>
                    {rectifyPreview.lrn && <span className="text-slate-500"> · LRN {rectifyPreview.lrn}</span>}
                    <span className="text-slate-500"> · {rectifyPreview.seller_name || rectifyPreview.seller_id}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {rectifyPreview.lane?.origin_zone} → {rectifyPreview.lane?.dest_zone} · ₹{rectifyPreview.lane?.base_rate}/kg · {rectifyPreview.lane?.billed_weight}kg
                  </Badge>
                </div>
                <div className="text-xs text-slate-400">
                  Agreement card: {rectifyPreview.card?.title} (effective {rectifyPreview.card?.effective_from})
                </div>

                {/* old vs new */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Current charge</div>
                    <div className="text-lg font-bold text-slate-800">{rupee(rectifyPreview.old.seller_final_amount)}</div>
                  </div>
                  <div className="rounded-lg bg-[#00A699]/5 border border-[#00A699]/20 px-3 py-2">
                    <div className="text-xs text-slate-500">Agreement charge (new)</div>
                    <div className="text-lg font-bold" style={{ color: "#00A699" }}>{rupee(rectifyPreview.new.seller_final_amount)}</div>
                  </div>
                  <div className={`rounded-lg px-3 py-2 ${rectifyPreview.difference > 0.01 ? "bg-red-50" : rectifyPreview.difference < -0.01 ? "bg-green-50" : "bg-slate-50"}`}>
                    <div className="text-xs text-slate-500">Wallet {rectifyPreview.difference > 0 ? "debit" : rectifyPreview.difference < 0 ? "credit" : "change"}</div>
                    <div className={`text-lg font-bold ${rectifyPreview.difference > 0.01 ? "text-red-600" : rectifyPreview.difference < -0.01 ? "text-green-600" : "text-slate-400"}`}>
                      {rectifyPreview.difference > 0 ? "+" : ""}{rupee(rectifyPreview.difference)}
                    </div>
                  </div>
                </div>

                {/* new breakdown */}
                {rectifyPreview.new.seller_charges?.fwd_charges?.breakdown && (
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500"><tr>
                        <th className="text-left px-3 py-1.5">Agreement component</th>
                        <th className="text-right px-3 py-1.5">₹</th>
                      </tr></thead>
                      <tbody>
                        {Object.entries(rectifyPreview.new.seller_charges.fwd_charges.breakdown).map(([k, v]) => (
                          <tr key={k} className="border-t">
                            <td className="px-3 py-1.5 capitalize">{k.replace(/_/g, " ")}</td>
                            <td className="px-3 py-1.5 text-right">{rupee(v.seller_value)}</td>
                          </tr>
                        ))}
                        <tr className="border-t bg-slate-50 font-medium">
                          <td className="px-3 py-1.5">Pre-tax</td>
                          <td className="px-3 py-1.5 text-right">{rupee(rectifyPreview.new.seller_charges.fwd_charges.pre_tax_freight)}</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-3 py-1.5">GST ({rectifyPreview.new.seller_charges.fwd_charges.gst_percent}%)</td>
                          <td className="px-3 py-1.5 text-right">{rupee(rectifyPreview.new.seller_charges.fwd_charges.gst)}</td>
                        </tr>
                        <tr className="border-t bg-slate-50 font-semibold">
                          <td className="px-3 py-1.5">Total (agreement)</td>
                          <td className="px-3 py-1.5 text-right">{rupee(rectifyPreview.new.seller_final_amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* wallet + confirm */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-slate-500">
                    Wallet balance {rupee(rectifyPreview.wallet.balance)} → <span className="font-medium text-slate-700">{rupee(rectifyPreview.wallet.projected_balance_after)}</span>
                    {!rectifyPreview.billed && <span className="ml-2 text-amber-600">(not yet billed — applies at billing)</span>}
                  </div>
                  <Button
                    onClick={applyRectify}
                    disabled={rectifyApplying || !rectifyPreview.changed}
                    style={{ backgroundColor: rectifyPreview.changed ? "#00A699" : undefined }}
                    className="hover:opacity-90"
                  >
                    {rectifyApplying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />}
                    {rectifyPreview.changed ? "Confirm & adjust wallet" : "Already at agreement"}
                  </Button>
                </div>
              </div>
            )}
            </>
            )}

            {rectifyMode === "bulk" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Seller</Label>
                  <Select value={bulkSeller || "none"} onValueChange={(v) => { setBulkSeller(v === "none" ? "" : v); setBulkPreview(null); }}>
                    <SelectTrigger><SelectValue placeholder="Select a seller" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a seller</SelectItem>
                      {sellers.map((s) => (
                        <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.company_registered_name || s.seller_id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} />
                </div>
              </div>
              <div>
                <Button onClick={previewBulk} disabled={bulkLoading || !bulkSeller} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                  {bulkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Preview all
                </Button>
              </div>

              {bulkPreview && (
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="text-sm font-medium text-slate-900">{bulkPreview.seller_name} — {bulkPreview.total_orders} forward order(s) scanned</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">To rectify</div><div className="text-lg font-bold text-slate-800">{bulkPreview.pricable_count}</div></div>
                    <div className="rounded-lg bg-red-50 px-3 py-2"><div className="text-xs text-slate-500">Debits</div><div className="text-lg font-bold text-red-600">{bulkPreview.to_debit}</div></div>
                    <div className="rounded-lg bg-green-50 px-3 py-2"><div className="text-xs text-slate-500">Credits</div><div className="text-lg font-bold text-green-600">{bulkPreview.to_credit}</div></div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">Skipped</div><div className="text-lg font-bold text-slate-500">{bulkPreview.skipped_count}</div></div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-slate-500">Current total <span className="font-medium text-slate-800">{rupee(bulkPreview.total_current)}</span></span>
                    <span className="text-slate-500">Agreement total <span className="font-medium text-slate-800">{rupee(bulkPreview.total_agreement)}</span></span>
                    <span className={`font-semibold ${bulkPreview.net_difference > 0.01 ? "text-red-600" : bulkPreview.net_difference < -0.01 ? "text-green-600" : "text-slate-400"}`}>
                      Net wallet {bulkPreview.net_difference > 0 ? "debit" : bulkPreview.net_difference < 0 ? "credit" : "change"} {bulkPreview.net_difference > 0 ? "+" : ""}{rupee(bulkPreview.net_difference)}
                    </span>
                  </div>
                  {bulkPreview.skipped_by_reason?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {bulkPreview.skipped_by_reason.map((s) => (
                        <Badge key={s.reason} className="bg-amber-100 text-amber-700" title={s.message}>{s.message} ({s.count})</Badge>
                      ))}
                    </div>
                  )}
                  {bulkPreview.over_cap && (
                    <div className="text-xs text-amber-600">More than {bulkPreview.cap} orders match — narrow the date range before applying.</div>
                  )}

                  {/* Per-order detail — LRN-wise calculation, selectable + exportable */}
                  {bulkPreview.rows?.length > 0 && (
                    <div className="rounded-lg border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                        <span className="text-xs font-medium text-slate-600">
                          Per-order detail{bulkPreview.rows_truncated ? ` (showing first ${bulkPreview.rows.length} of ${bulkPreview.total_orders})` : ""}
                        </span>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={exportBulkCsv} disabled={exporting}>
                          {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                          {exporting ? "Preparing…" : "Export CSV"}
                        </Button>
                      </div>
                      <div className="max-h-96 overflow-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white text-slate-500 sticky top-0 shadow-sm">
                            <tr className="border-b">
                              <th className="px-2 py-1.5 w-8 text-center">
                                <input type="checkbox" aria-label="Select all"
                                  checked={selectableIds().length > 0 && selectableIds().every((id) => selectedIds.has(id))}
                                  onChange={toggleAll} />
                              </th>
                              <th className="text-left px-2 py-1.5">Order / LRN</th>
                              <th className="text-left px-2 py-1.5">Lane</th>
                              <th className="text-right px-2 py-1.5">Current</th>
                              <th className="text-right px-2 py-1.5">Agreement</th>
                              <th className="text-right px-2 py-1.5">Difference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkPreview.rows.map((r) => (
                              <tr key={r.order_id || r.order_number}
                                  className={`border-t ${!r.pricable ? "bg-amber-50/40" : "hover:bg-slate-50 cursor-pointer"}`}
                                  onClick={() => openRowDetail(r)}>
                                <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                                  {r.pricable && Math.abs(r.difference) > 0.01 ? (
                                    <input type="checkbox" aria-label={`Select ${r.order_number}`}
                                      checked={selectedIds.has(r.order_id)} onChange={() => toggleRow(r.order_id)} />
                                  ) : null}
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="flex items-center gap-1.5">
                                    {r.pricable && <Eye className="w-3.5 h-3.5 shrink-0 text-slate-300" />}
                                    <div>
                                      <div className="font-medium text-slate-800 whitespace-nowrap">{r.order_number}</div>
                                      {r.lrn && (
                                        <a href={`/admin/orders/${r.order_id}`} target="_blank" rel="noopener noreferrer"
                                           onClick={(e) => e.stopPropagation()}
                                           className="text-xs text-[#00A699] hover:underline inline-flex items-center gap-0.5">
                                          LRN {r.lrn}<ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 text-xs text-slate-500">
                                  {r.pricable ? (
                                    <span className="whitespace-nowrap">{r.lane?.origin_zone} → {r.lane?.dest_zone} · ₹{r.lane?.base_rate}/kg · {r.lane?.billed_weight}kg</span>
                                  ) : (
                                    <span className="text-amber-700">{r.message || r.reason}</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{r.pricable ? rupee(r.old) : "—"}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{r.pricable ? rupee(r.new) : "—"}</td>
                                <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap font-medium ${r.pricable && r.difference > 0.01 ? "text-red-600" : r.pricable && r.difference < -0.01 ? "text-green-600" : "text-slate-400"}`}>
                                  {r.pricable ? `${r.difference > 0 ? "+" : ""}${rupee(r.difference)}` : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-xs text-slate-500">Wallet balance {rupee(bulkPreview.wallet_balance)} → <span className="font-medium text-slate-700">{rupee(bulkPreview.projected_balance_after)}</span></div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={executeBulkSelected} disabled={!!bulkJobId || selectedIds.size === 0} className="hover:opacity-90">
                        <Wallet className="w-4 h-4 mr-2" /> Apply selected ({selectedIds.size})
                      </Button>
                      <Button onClick={executeBulk} disabled={!!bulkJobId || bulkPreview.pricable_count === 0 || bulkPreview.over_cap} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                        {bulkJobId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />} Confirm & apply to all ({bulkPreview.pricable_count})
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {bulkProgress && (
                <div className="rounded-lg bg-slate-50 p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize">{bulkProgress.status}</span>
                    <span className="text-slate-600">{bulkProgress.processed_orders || 0} / {bulkProgress.total_orders || 0}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full bg-[#00A699] transition-all" style={{ width: `${bulkProgress.progress_percentage || 0}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs pt-1">
                    <span className="text-[#00A699]">{bulkProgress.successful_orders || 0} adjusted</span>
                    <span className="text-slate-500">{bulkProgress.no_change || 0} no-change</span>
                    <span className="text-amber-600">{bulkProgress.skipped_orders || 0} skipped</span>
                    {bulkProgress.failed_orders ? <span className="text-red-600">{bulkProgress.failed_orders} failed</span> : null}
                    {bulkProgress.total_credited != null && <span className="text-green-600">{rupee(bulkProgress.total_credited)} credited</span>}
                    {bulkProgress.total_debited != null && <span className="text-red-600">{rupee(bulkProgress.total_debited)} debited</span>}
                  </div>
                </div>
              )}
            </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Results — tabbed: flagged overcharges vs verified/matched LRNs */}
        {mainView === "scan" && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Tabs */}
              <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("errors")}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === "errors" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Flagged errors
                  <Badge variant="secondary" className="ml-0.5">{summary.open_count}</Badge>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("matched")}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === "matched" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                >
                  <CheckCircle2 className="w-4 h-4 text-green-600" /> Matched LRNs
                  <Badge variant="secondary" className="ml-0.5">{matchCount}</Badge>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("skipped")}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === "skipped" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                >
                  <MinusCircle className="w-4 h-4 text-slate-400" /> Skipped
                  <Badge variant="secondary" className="ml-0.5">{skippedCount}</Badge>
                </button>
              </div>
              {/* Filters */}
              <div className="flex gap-2">
                {activeTab === "errors" && (
                  <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {Object.keys(STATUS_COLORS).map((s) => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={sellerFilter || "all"} onValueChange={(v) => setSellerFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9 w-[170px]"><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sellers</SelectItem>
                    {sellers.map((s) => (
                      <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.company_registered_name || s.seller_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {activeTab === "matched" && (
              <p className="text-xs text-slate-500 mt-2">
                LRNs the scan checked and found charged within the signed agreement — kept for reference. Open one for the full per-component bifurcation (our calculation vs what was charged).
              </p>
            )}
            {activeTab === "skipped" && (
              <p className="text-xs text-slate-500 mt-2">
                Orders the scan could not rate-check, with the reason. Filter by reason using the chips below.
              </p>
            )}
          </CardHeader>
          <CardContent>
            {activeTab === "errors" && (
              <DataTable
                columns={columns}
                data={errors}
                rowKey={(r) => r.id || r.order_id}
                loading={loading}
                empty={<EmptyState icon={Scale} title="No rate errors"
                  description="Run a scan to compare Delhivery's charges against the agreement rate card." />}
              />
            )}
            {activeTab === "matched" && (
              <DataTable
                columns={matchColumns}
                data={matches}
                rowKey={(r) => r.id || r.order_id}
                loading={matchesLoading}
                empty={<EmptyState icon={CheckCircle2} title="No matched LRNs yet"
                  description="Run a scan — orders charged within the agreement are kept here for future reference." />}
              />
            )}
            {activeTab === "skipped" && (
              <>
                {reasonBreakdown.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setReasonFilter("")}
                      className={`text-xs rounded-full px-3 py-1 border transition-colors ${!reasonFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                    >
                      All ({skippedCount})
                    </button>
                    {reasonBreakdown.map((rb) => (
                      <button
                        key={rb.reason_code}
                        type="button"
                        onClick={() => setReasonFilter(rb.reason_code === reasonFilter ? "" : rb.reason_code)}
                        className={`text-xs rounded-full px-3 py-1 border transition-colors ${reasonFilter === rb.reason_code ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`}
                      >
                        {reasonLabel(rb.reason_code)} ({rb.count})
                      </button>
                    ))}
                  </div>
                )}
                <DataTable
                  columns={skippedColumns}
                  data={skipped}
                  rowKey={(r) => r.id || r.order_id}
                  loading={skippedLoading}
                  empty={<EmptyState icon={MinusCircle} title="No skipped orders"
                    description="Run a scan — orders that couldn't be rate-checked are listed here with the reason." />}
                />
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Detail dialog — per-component drill-down + triage */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {detail?.__matched ? "Rate check" : "Rate error"} — {detail?.order_number || detail?.order_id}
                {detail && (detail.__matched
                  ? <Badge className="bg-green-100 text-green-700">MATCHED</Badge>
                  : <Badge className={STATUS_COLORS[detail.status] || "bg-slate-100"}>{(detail.status || "").replace("_", " ").toUpperCase()}</Badge>)}
              </DialogTitle>
              <DialogDescription>
                Validated against {detail?.card_title || "agreement"} (effective {detail?.effective_from}).
              </DialogDescription>
            </DialogHeader>
            {detail && (
              <div className="space-y-4 py-1">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Seller</span><div className="font-medium">{detail.seller_name || detail.seller_id || "—"}</div></div>
                  <div><span className="text-slate-500">LRN</span><div className="font-medium">{detail.lrn || "—"}</div></div>
                  <div><span className="text-slate-500">Lane</span><div className="font-medium inline-flex items-center gap-1">{detail.origin_zone} <ArrowRight className="w-3 h-3" /> {detail.dest_zone}</div></div>
                  <div><span className="text-slate-500">Agreement rate</span><div className="font-medium">₹{detail.base_rate}/kg · {detail.billed_weight}kg · {detail.rate_source}</div></div>
                </div>

                <div>
                  <div className="text-sm font-medium mb-1">
                    {breakdown ? "Freight breakdown — Agreement vs Delhivery" : "Component comparison"}
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">Component</th>
                          <th className="text-right px-3 py-2">Agreement</th>
                          <th className="text-right px-3 py-2">Delhivery</th>
                          <th className="text-right px-3 py-2">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown ? breakdown.rows.map((r, i) => {
                          const bold = r.group === "subtotal" || r.group === "total";
                          return (
                            <tr key={i} className={`border-t ${bold ? "bg-slate-50 font-semibold" : ""} ${r.tag === "flagged" ? "bg-red-50" : ""}`}>
                              <td className="px-3 py-2 align-top">
                                {r.label}
                                {r.tag === "flagged" && <span className="ml-1.5 text-[10px] font-semibold text-red-600">FLAGGED</span>}
                                {r.tag === "info" && <span className="ml-1.5 text-[10px] text-slate-400">info only</span>}
                                {r.remark && <div className="text-[11px] font-normal italic text-slate-400 mt-0.5 max-w-xs whitespace-normal">{r.remark}</div>}
                              </td>
                              <td className="px-3 py-2 text-right align-top">
                                <div>{r.agreement == null ? "—" : rupee(r.agreement)}</div>
                                {r.agreement_formula && (
                                  <div className="text-[11px] font-normal text-slate-400 whitespace-nowrap">{r.agreement_formula}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right align-top">
                                <div>{r.delhivery == null ? "—" : rupee(r.delhivery)}</div>
                                {r.delhivery_formula && (
                                  <div className="text-[11px] font-normal text-slate-400 whitespace-nowrap">{r.delhivery_formula}</div>
                                )}
                              </td>
                              <td className={`px-3 py-2 text-right align-top font-medium ${r.delta == null ? "text-slate-300" : r.delta > 0.01 ? "text-red-600" : r.delta < -0.01 ? "text-green-600" : "text-slate-400"}`}>
                                {r.delta == null ? "—" : (r.delta > 0 ? "+" : "") + rupee(r.delta)}
                              </td>
                            </tr>
                          );
                        }) : (
                          <>
                            {(detail.mismatched_components || []).map((c) => (
                              <tr key={c.component} className="border-t">
                                <td className="px-3 py-2">{c.component}</td>
                                <td className="px-3 py-2 text-right">{rupee(c.expected)}</td>
                                <td className="px-3 py-2 text-right">{rupee(c.actual)}</td>
                                <td className={`px-3 py-2 text-right font-medium ${c.delta > 0 ? "text-red-600" : "text-green-600"}`}>
                                  {c.delta > 0 ? "+" : ""}{rupee(c.delta)}
                                </td>
                              </tr>
                            ))}
                            <tr className="border-t bg-slate-50 font-semibold">
                              <td className="px-3 py-2" colSpan={3}>Net overcharge</td>
                              <td className={`px-3 py-2 text-right ${detail.net_overcharge > 0 ? "text-red-600" : "text-green-600"}`}>
                                {detail.net_overcharge > 0 ? "+" : ""}{rupee(detail.net_overcharge)}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {detail.__matched ? (
                    <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-green-100 bg-green-50 px-3 py-2">
                      <div>
                        <div className="text-xs text-slate-500">Zone rate verified — charged within agreement</div>
                        <div className="text-lg font-bold text-green-700 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> No overcharge
                        </div>
                      </div>
                      {breakdown && (
                        <div className="sm:text-right text-xs text-slate-600">
                          <div className="text-slate-500">Total freight</div>
                          <div className="text-slate-400">agreement {rupee(breakdown.total_expected)} · billed {rupee(breakdown.total_billed)}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                      <div>
                        <div className="text-xs text-slate-500">Flagged overcharge (claimable — zone rate)</div>
                        <div className="text-lg font-bold text-red-600">{detail.net_overcharge > 0 ? "+" : ""}{rupee(detail.net_overcharge)}</div>
                      </div>
                      {breakdown && (
                        <div className="sm:text-right text-xs text-slate-600">
                          <div className="text-slate-500">With fuel &amp; GST cascade on the lane rate</div>
                          <div className="text-base font-bold text-red-600">+{rupee(breakdown.cascade_over_billing)}</div>
                          <div className="text-slate-400">agreement {rupee(breakdown.total_expected)} · billed {rupee(breakdown.total_billed)}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-slate-400 mt-1">
                    {bdLoading ? "Loading full breakdown…"
                      : detail.__matched ? "Δ per component = Delhivery vs the agreement. The zone rate was verified within the agreement, so nothing is flagged; conditional charges (green, ODA, insurance, handling) are shown for transparency."
                      : breakdown ? "Positive Δ = Delhivery charged more than the agreement. Only the zone-rate overcharge is flagged/claimed; fuel & GST cascade off it. Conditional charges (green, ODA, insurance, handling) are shown for transparency and aren't independently flagged."
                      : "Positive Δ = Delhivery charged us more than the agreement."}
                  </p>
                </div>

                {(detail.action_logs || []).length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-1">History</div>
                    <ul className="space-y-1 text-xs text-slate-600">
                      {detail.action_logs.map((l, i) => (
                        <li key={i} className="flex justify-between gap-2 border-b py-1">
                          <span>{(l.to_status || l.action_type || "").replace("_", " ")}{l.notes ? ` — ${l.notes}` : ""}</span>
                          <span className="text-slate-400 whitespace-nowrap">{l.by} · {formatToIST(l.at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!detail.__matched && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-xs">Take action</Label>
                    <Select value={actionType} onValueChange={setActionType}>
                      <SelectTrigger><SelectValue placeholder="Choose an action…" /></SelectTrigger>
                      <SelectContent>
                        {(NEXT_ACTIONS[detail.status] || []).map(([val, lbl]) => (
                          <SelectItem key={val} value={val}>{lbl}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Notes (optional)" value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} rows={2} />
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              {!detail?.__matched && (
                <Button onClick={applyAction} disabled={!actionType || applying} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                  {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Apply
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bifurcation modal — the full NEW-agreement breakdown for one rectification row */}
        <Dialog open={!!detailModalRow} onOpenChange={(o) => { if (!o) setDetailModalRow(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                Agreement bifurcation — {detailModalRow?.order_number || detailModalRow?.order_id}
                {detailModalRow?.pricable && (
                  <span className={`text-sm font-semibold ${detailModalRow.difference > 0.01 ? "text-red-600" : detailModalRow.difference < -0.01 ? "text-green-600" : "text-slate-400"}`}>
                    {detailModalRow.difference > 0 ? "+" : ""}{rupee(detailModalRow.difference)}{detailModalRow.difference > 0.01 ? " debit" : detailModalRow.difference < -0.01 ? " credit" : ""}
                  </span>
                )}
              </DialogTitle>
              {detailModalRow?.lrn && (
                <DialogDescription>
                  <a href={`/admin/orders/${detailModalRow.order_id}`} target="_blank" rel="noopener noreferrer"
                     className="text-[#00A699] hover:underline inline-flex items-center gap-0.5">
                    LRN {detailModalRow.lrn} <ExternalLink className="w-3 h-3" />
                  </a>
                </DialogDescription>
              )}
            </DialogHeader>
            {detailModalRow && (
              !rowDetail[detailModalRow.order_id] ? (
                <div className="text-sm text-slate-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading bifurcation…</div>
              ) : (() => {
                const d = rowDetail[detailModalRow.order_id];
                const fc = d?.new?.seller_charges?.fwd_charges || {};
                const bd = fc.breakdown || {};
                return (
                  <div className="space-y-3 py-1">
                    <div className="text-xs text-slate-500">
                      Agreement card: <span className="font-medium">{d?.card?.title}</span>{d?.card?.effective_from ? ` (effective ${d.card.effective_from})` : ""}
                      <br />Lane {d?.lane?.origin_zone} → {d?.lane?.dest_zone} · ₹{d?.lane?.base_rate}/kg · {d?.lane?.billed_weight}kg
                    </div>
                    <div className="rounded-lg border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500"><tr>
                          <th className="text-left px-3 py-2 font-medium">Component</th>
                          <th className="text-left px-3 py-2 font-medium">Agreement term</th>
                          <th className="text-right px-3 py-2 font-medium">Amount</th>
                        </tr></thead>
                        <tbody>
                          {Object.entries(bd).map(([k, v]) => (
                            <tr key={k} className="border-t align-top">
                              <td className="px-3 py-2 capitalize pr-3 whitespace-nowrap">{k.replace(/_/g, " ")}</td>
                              <td className="px-3 py-2 pr-3">
                                {v.basis && <span className="text-slate-600">{v.basis}</span>}
                                {v.remark && <div className="text-[11px] italic text-slate-400">{v.remark}</div>}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{rupee(v.seller_value)}</td>
                            </tr>
                          ))}
                          <tr className="border-t bg-slate-50 font-medium"><td className="px-3 py-2">Pre-tax</td><td /><td className="px-3 py-2 text-right tabular-nums">{rupee(fc.pre_tax_freight)}</td></tr>
                          <tr className="border-t"><td className="px-3 py-2">GST ({fc.gst_percent}%)</td><td /><td className="px-3 py-2 text-right tabular-nums">{rupee(fc.gst)}</td></tr>
                          <tr className="border-t bg-slate-50 font-semibold"><td className="px-3 py-2">Total (agreement)</td><td /><td className="px-3 py-2 text-right tabular-nums">{rupee(d?.new?.seller_final_amount)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">Current (billed)</div><div className="font-bold text-slate-800">{rupee(detailModalRow.old)}</div></div>
                      <div className="rounded-lg bg-[#00A699]/5 border border-[#00A699]/20 px-3 py-2"><div className="text-xs text-slate-500">Agreement (new)</div><div className="font-bold" style={{ color: "#00A699" }}>{rupee(detailModalRow.new)}</div></div>
                      <div className={`rounded-lg px-3 py-2 ${detailModalRow.difference > 0.01 ? "bg-red-50" : detailModalRow.difference < -0.01 ? "bg-green-50" : "bg-slate-50"}`}><div className="text-xs text-slate-500">Wallet {detailModalRow.difference > 0 ? "debit" : detailModalRow.difference < 0 ? "credit" : "change"}</div><div className={`font-bold ${detailModalRow.difference > 0.01 ? "text-red-600" : detailModalRow.difference < -0.01 ? "text-green-600" : "text-slate-400"}`}>{detailModalRow.difference > 0 ? "+" : ""}{rupee(detailModalRow.difference)}</div></div>
                    </div>
                  </div>
                );
              })()
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailModalRow(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RateErrors;
